import React from "react";
import PageHero, { PageCTA } from "../components/PageHero";
import { Phone, Clock, ShieldCheck, MapPin, Check } from "lucide-react";
import { heroImages, emergencySteps, companyInfo } from "../data/mock";

const OuverturePorte = () => {
  return (
    <div>
      <PageHero
        title="Ouverture de porte Fichet"
        subtitle="Porte claquée, clé perdue ou cassée ? Nos techniciens spécialistes Fichet interviennent 24h/24 sans dégât."
        breadcrumb="Ouverture de porte"
        badge="Intervention 24/7"
        image={heroImages.ouverturePorte}
      />

      {/* URGENCY BANNER */}
      <section className="bg-[#123B2E] text-white py-8">
        <div className="max-w-[1400px] mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-[#C9A227] flex items-center justify-center flex-shrink-0">
              <Clock className="w-7 h-7 text-black" />
            </div>
            <div>
              <div className="text-xl font-black tracking-tight">Intervention en moins de 45 minutes</div>
              <div className="text-sm text-gray-300">7 jours sur 7 — 24 heures sur 24 — partout en France</div>
            </div>
          </div>
          <a href={`tel:${companyInfo.emergencyPhone.replace(/\s/g, "")}`} className="inline-flex items-center gap-3 bg-[#C9A227] text-black font-bold px-6 py-4 hover:bg-[#A8861C] transition-colors">
            <Phone className="w-5 h-5" />
            Appeler maintenant
          </a>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <div className="text-sm font-bold text-[#8C6F14] tracking-widest uppercase mb-3">Notre processus</div>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight text-black">Comment se déroule l'intervention ?</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {emergencySteps.map((step, i) => (
              <div key={i} className="relative">
                <div className="text-6xl font-black text-[#C9A227] mb-3 tracking-tight">{step.number}</div>
                <h3 className="text-lg font-bold text-black mb-2 tracking-tight">{step.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <img src={heroImages.ouverturePorte} alt="Ouverture de porte" className="w-full h-[460px] object-cover" />
          </div>
          <div>
            <div className="text-sm font-bold text-[#8C6F14] tracking-widest uppercase mb-3">Un savoir-faire unique</div>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight text-black leading-tight mb-6">
              Ouverture sans dégât garantie
            </h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Nos techniciens maîtrisent les techniques d'ouverture fine sur toutes les serrures, avec une spécialisation Fichet : Fichet, Vachette, Bricard, Picard, Muel, Mottura et plus encore.
            </p>
            <ul className="space-y-3">
              {[
                "Techniques non destructives sur porte claquée",
                "Diagnostic préalable et devis transparent",
                "Serruriers certifiés et assurés",
                "Prise en charge assurance possible",
                "Facture détaillée après intervention",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#C9A227] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-black" />
                  </div>
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SITUATIONS */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <div className="text-sm font-bold text-[#8C6F14] tracking-widest uppercase mb-3">Toutes les situations</div>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight text-black">Dans quels cas nous appeler ?</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: "Porte claquée", desc: "Vous êtes sorti sans vos clés et la porte s'est fermée derrière vous." },
              { title: "Clé perdue ou volée", desc: "Nous ouvrons la porte et changeons votre cylindre pour votre sécurité." },
              { title: "Clé cassée dans la serrure", desc: "Extraction de la clé sans endommager le mécanisme de votre serrure." },
              { title: "Serrure bloquée", desc: "Déblocage mécanique ou remplacement selon l'état du mécanisme." },
              { title: "Verrou gripé", desc: "Intervention pour débloquer un verrou ou pêne gripé." },
              { title: "Après cambriolage", desc: "Ouverture, sécurisation immédiate et remplacement du bloc-porte." },
            ].map((s, i) => (
              <div key={i} className="p-6 border border-gray-200 hover:border-[#C9A227] hover:shadow-lg transition-all">
                <ShieldCheck className="w-8 h-8 text-[#8C6F14] mb-3" />
                <h3 className="text-lg font-bold text-black mb-2 tracking-tight">{s.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageCTA title="Bloqué devant votre porte ?" subtitle="Un serrurier Fichet intervient chez vous en moins d'une heure" />
    </div>
  );
};

export default OuverturePorte;
